// Predicado ÚNICO de "por pagar" a nivel FACTURA SII recibida (regla 10.1 / 10.106).
//
// Fuente de verdad compartida entre FlujoCajaV2 (panel "Por pagar" + badge
// countPorPagarPendientes) y EstadoCuenta (contador de facturas por pagar por proveedor).
// Antes vivía inline en FlujoCajaV2 y estaba DUPLICADO inline en EstadoCuenta (Fase 3);
// se consolidó acá (Fase 4) para que ambos no puedan divergir ante un nuevo gate.
//
// NO gatea por estado_vinculacion (regla 10.65): una factura "vinculada" pagada solo
// parcialmente conserva saldo FIFO y debe contar/mostrarse; la exclusión de las pagadas la
// hace el FIFO (ausencia del key en saldosPorFacturaMap). Excluye: históricas pre-corte, NC
// (tipo_dte=61, regla 10.42), DIN (tipo_dte=914 — Aduana, no es deuda de proveedor), factura
// ANULADA por una NC 'anula_error' (regla 10.222 — el par NC↔factura se cancela entero, igual
// que en el ER), caja chica (10.39), comisiones factoring/recepción neteadas (10.80/10.81),
// mora saldada por neteo, y facturas saldadas al 100% por el ledger FIFO.
//
// Los sets y el map se reciben por parámetro (cada consumidor ya los tiene armados con SU
// data): la función es pura (solo importa el helper puro esNotaCredito). saldosPorFacturaMap está keyed "SII:{id}".
// `facturasAnuladasAnulaError` = ids de facturas que son documento_referenciado_id de una NC
// 'anula_error' (construirExclusionNcAnulaError). Default vacío → sin exclusión (backward-compat).
import { esDeclaracionIngreso, esNotaCredito } from "./dte";

export const facturaSiiPorPagar = (
  f,
  {
    cajaChicaFacturaIds,
    facturasComisionFactoringSet,
    facturasMoraNeteadaSet,
    saldosPorFacturaMap,
    FECHA_CORTE,
    facturasAnuladasAnulaError = new Set(),
  }
) => {
  // Pieza A (regla 10.29): una pre-corte "despertada" (residual_corte > 0) SÍ es obligación pagable con
  // folio → no la cortamos acá; su saldo real lo decide el FIFO (map). Las demás pre-corte siguen dormidas.
  if (f.fecha_emision && f.fecha_emision < FECHA_CORTE && !(Number(f.residual_corte) > 0)) return false;
  if (esNotaCredito(f)) return false;
  // DIN (Declaración de Ingreso, Aduana): su monto_total no es deuda de proveedor (caso Polywell/
  // importación 2026-09, ver docs/decisiones). Fuera de "Por pagar" por tipo de documento.
  if (esDeclaracionIngreso(f)) return false;
  // Factura anulada por una NC 'anula_error' (regla 10.222): el par se cancela entero, no es deuda.
  // Defensa en profundidad — el ledger ya la saca del FIFO (ausencia del key), esto cubre además el
  // caso sin proveedor_id (que abajo cae en `return true`). NO aplica a 'ajuste_comercial'.
  if (facturasAnuladasAnulaError.has(f.id)) return false;
  // Ciclo de vida SII (regla 10.154, OVERRIDE 2026-07-31): una recibida 'pendiente' (en
  // obligations/pending, sin acuse — Ley 19.983) SÍ es obligación real del lado caja — a menudo ya
  // pagada por débito — y se trata como pasivo firme igual que 'aceptada'. Solo 'rechazada' (rechazo
  // confirmado, nunca será deuda) queda fuera de "Por pagar". Las pendientes ya pagadas igual no
  // aparecen: las corta el gate FIFO (g). El lado impositivo no se toca (IVA crédito las incluye, P2.20).
  if (f.estado_sii === "rechazada") return false;
  if (cajaChicaFacturaIds.has(f.id)) return false;
  if (facturasComisionFactoringSet.has(f.id)) return false;
  // Mora saldada por neteo: el DTE se descontó del giro de una op posterior, sin débito
  // bancario → el FIFO la deja viva. Gate ANTES del check de proveedor_id (estas facturas
  // tienen proveedor_id y si no, caerían en el FIFO y seguirían apareciendo).
  if (facturasMoraNeteadaSet.has(f.id)) return false;
  // Con proveedor_id: ausente del map FIFO = saldada al 100% → excluir. Sin proveedor_id
  // no hay FIFO → se asume monto total pendiente, siempre incluye.
  if (f.proveedor_id) return saldosPorFacturaMap.get(`SII:${f.id}`) !== undefined;
  return true;
};
